import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Download, FileText, File as FileIcon, Image as ImageIcon } from 'lucide-react';
import { useEventFiles, useUploadEventFile, useDeleteEventFile } from '@/hooks/useEventFiles';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface EventFilesCellProps {
  eventType: 'interaction' | 'follow-up';
  eventId: string;
  canUpload?: boolean;
}

export function EventFilesCell({ eventType, eventId, canUpload = false }: EventFilesCellProps) {
  const { data: files = [], isLoading } = useEventFiles(eventType, eventId);
  const uploadMutation = useUploadEventFile();
  const deleteMutation = useDeleteEventFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setUploading(true);

    try {
      // Upload directly via hook - no manual storage upload needed
      await uploadMutation.mutateAsync({
        eventType,
        eventId,
        file: selectedFile,
      });
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (file: any) => {
    if (!confirm(`Delete ${file.file_name}?`)) return;

    await deleteMutation.mutateAsync({
      storagePath: file.storage_path,
      eventType,
      eventId,
    });
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return ImageIcon;
    if (fileType.includes('pdf')) return FileText;
    return FileIcon;
  };

  const renderThumbnail = (file: any) => {
    if (file.file_type.startsWith('image/')) {
      return (
        <img
          src={file.thumbnail_url || file.file_url}
          alt={file.file_name}
          className="w-8 h-8 rounded object-cover"
          onError={(e) => {
            e.currentTarget.src = file.file_url;
          }}
        />
      );
    } else {
      const Icon = getFileIcon(file.file_type);
      return (
        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
      );
    }
  };

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="flex items-center gap-2">
      {files.length > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-1.5">
              <FileIcon className="h-3.5 w-3.5" />
              <span className="text-xs">{files.length}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-2">
              <div className="font-medium text-sm">Attached Files ({files.length})</div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50"
                  >
                    {renderThumbnail(file)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" title={file.file_name}>
                        {file.file_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(file.file_url, '_blank')}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {canUpload && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(file)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {canUpload && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="h-3.5 w-3.5" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          />
        </>
      )}
    </div>
  );
}
